// pages/partner/PartnerSpecialtyDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";

const FILTERS = [
  { key: "all", icon: "" },
  { key: "pending", icon: "⏳" },
  { key: "seen", icon: "👁️" },
  { key: "answered", icon: "✅" },
  { key: "done", icon: "🎉" },
];

// Helpers safe
function fmtDate(d, locale) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(d);
  }
}

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "seen") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "answered") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "done") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

// Renderers métier: ici tu “étoffes”
// Ils ne doivent JAMAIS faire planter l’écran même si des champs manquent
const SpecialtyRenderers = {
  insurance: (req, t, locale) => {
    const p = req.payload || {};
    const artwork = p.artwork || {};
    const client = p.client || {};
    const insuredValue = p.insured_value || p.value || null;

    return (
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-medium text-gray-900">
          {artwork.title || p.artwork_title || t("partner.common.artwork")}{" "}
          <span className="text-gray-500">
            {artwork.artist || p.artwork_artist ? `• ${artwork.artist || p.artwork_artist}` : ""}
          </span>
        </div>
        <div className="text-gray-600">
          {t("partner.common.client")}: {client.name || p.client_name || "—"}
        </div>
        <div className="text-gray-600">
          {t("partner.requests.insuredValue")}: {insuredValue ? `${insuredValue} €` : "—"}
        </div>
        <div className="text-gray-500 text-xs">
          {t("partner.requests.insuranceExpected")}
        </div>
      </div>
    );
  },

  transport: (req, t, locale) => {
    const p = req.payload || {};
    const artwork = p.artwork || {};
    const pickup = p.pickup || {};
    const delivery = p.delivery || {};

    return (
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-medium text-gray-900">
          {artwork.title || p.artwork_title || t("partner.transport.defaultRequest")}{" "}
          <span className="text-gray-500">
            {artwork.artist || p.artwork_artist ? `• ${artwork.artist || p.artwork_artist}` : ""}
          </span>
        </div>
        <div className="text-gray-600">
          {t("partner.requests.pickup")}: {pickup.city || p.pickup_city || "—"}{" "}
          {pickup.date ? `(${fmtDate(pickup.date, locale)})` : ""}
        </div>
        <div className="text-gray-600">
          {t("partner.requests.delivery")}: {delivery.city || p.delivery_city || "—"}{" "}
          {delivery.date ? `(${fmtDate(delivery.date, locale)})` : ""}
        </div>
        <div className="text-gray-500 text-xs">
          {t("partner.requests.transportExpected")}
        </div>
      </div>
    );
  },

  valuation_expert: (req, t) => {
    const p = req.payload || {};
    const artwork = p.artwork || {};
    return (
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-medium text-gray-900">
          {artwork.title || p.artwork_title || t("partner.valuation.requestTitle")}{" "}
          <span className="text-gray-500">
            {artwork.artist || p.artwork_artist ? `• ${artwork.artist || p.artwork_artist}` : ""}
          </span>
        </div>
        <div className="text-gray-600">{t("partner.insurance.context")}: {p.context || "—"}</div>
        <div className="text-gray-500 text-xs">
          {t("partner.requests.valuationExpected")}
        </div>
      </div>
    );
  },

  conservation_expert: (req, t) => {
    const p = req.payload || {};
    const artwork = p.artwork || {};
    return (
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-medium text-gray-900">
          {artwork.title || p.artwork_title || t("partner.requests.conservationRequest")}{" "}
          <span className="text-gray-500">
            {artwork.artist || p.artwork_artist ? `• ${artwork.artist || p.artwork_artist}` : ""}
          </span>
        </div>
        <div className="text-gray-600">{t("partner.requests.issue")}: {p.issue || p.problem || "—"}</div>
        <div className="text-gray-500 text-xs">
          {t("partner.requests.conservationExpected")}
        </div>
      </div>
    );
  },

  general: (req, t) => {
    const p = req.payload || {};
    return (
      <div className="text-sm text-gray-700 space-y-1">
        <div className="font-medium text-gray-900">
          {p.title || t("partner.requests.request")}
        </div>
        <div className="text-gray-600">
          {p.summary || p.message || "—"}
        </div>
      </div>
    );
  },
};

export default function PartnerSpecialtyDashboard({
  specialtyKey = "general",
  title,
  subtitle,
  description,
  typeFilter = ["general"],
  accent = "gray",
  partnership = null,
}) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [err, setErr] = useState("");

  const renderer = useMemo(() => SpecialtyRenderers[specialtyKey] || SpecialtyRenderers.general, [specialtyKey]);

  async function load() {
    try {
      setLoading(true);
      setErr("");

      const { data: userRes, error: uErr } = await supabase.auth.getUser();
      if (uErr) throw uErr;
      const user = userRes?.user;
      if (!user?.id) throw new Error("No user session");

      // IMPORTANT: on ne joint PAS artworks ici (évite les erreurs de colonnes)
      let query = supabase
        .from("partner_requests")
        .select("id,created_at,status,type,payload,notes,taken_at,done_at,expert_id")
        .eq("expert_id", user.id)
        .in("type", typeFilter)
        .order("created_at", { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      setRequests(data || []);
    } catch (e) {
      console.error("[PartnerSpecialtyDashboard] load error:", e);
      setErr(e?.message || t("partner.dashboard.loadError"));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(typeFilter)]);

  const filteredRequests = useMemo(
    () => filter === "all" ? requests : requests.filter((request) => request.status === filter),
    [filter, requests],
  );
  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((request) => request.status === "pending").length,
    active: requests.filter((request) => ["seen", "answered", "quoted", "accepted"].includes(request.status)).length,
    done: requests.filter((request) => request.status === "done").length,
  }), [requests]);

  const accentClasses =
    accent === "amber"
      ? "border-amber-200 bg-amber-50"
      : accent === "blue"
      ? "border-blue-200 bg-blue-50"
      : accent === "emerald"
      ? "border-emerald-200 bg-emerald-50"
      : "border-gray-200 bg-gray-50";

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6 md:py-8">
      <section className={`overflow-hidden rounded-2xl border ${accentClasses}`}>
        <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)] md:p-8">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">{t("partner.dashboard.eyebrow")}</p>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-950">{title || t("partner.dashboard.title")}</h1>
            <p className="mt-2 text-base text-gray-600">{subtitle || t("partner.dashboard.subtitle")}</p>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-gray-700">{description || t("partner.dashboard.intro")}</p>
          </div>
          <div className="rounded-xl border border-white/90 bg-white/75 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">{t("partner.dashboard.partnershipTitle")}</p>
            {partnership ? <div className="mt-3 space-y-2 text-sm text-gray-700">
              <Line label={t("partner.dashboard.partnershipStatus")} value={t(`partner.dashboard.partnerStatus.${partnership.status}`, { defaultValue: partnership.status })} />
              <Line label={t("partner.dashboard.accessMode")} value={t(`partner.dashboard.accessModes.${partnership.access_mode}`, { defaultValue: partnership.access_mode })} />
              <Line label={t("partner.dashboard.relationship")} value={t(`partner.dashboard.relationships.${partnership.relationship_type}`, { defaultValue: partnership.relationship_type })} />
              <Line label={t("partner.dashboard.validUntil")} value={partnership.ends_at ? fmtDate(partnership.ends_at, i18n.language) : t("partner.dashboard.noExpiry")} />
              <div className="pt-2"><Link to="/partner/profile" className="text-xs font-medium underline">{t("partner.dashboard.manageProfile")}</Link></div>
            </div> : <p className="mt-3 text-sm leading-6 text-gray-600">{t("partner.dashboard.noPartnershipRecord")}</p>}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[["total", stats.total],["pending", stats.pending],["active", stats.active],["done", stats.done]].map(([key,value]) => (
          <div key={key} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t(`partner.dashboard.metrics.${key}`)}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-950">{loading ? "…" : value}</p>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={[
                "px-3 py-2 rounded-lg text-sm font-medium transition",
                filter === f.key ? "bg-black text-white" : "bg-white border border-gray-200 hover:bg-gray-50",
              ].join(" ")}
            >
              {f.icon ? `${f.icon} ` : ""}{t(`partner.filters.${f.key}`)}
            </button>
          ))}
          <button
            onClick={() => load()}
            className="ml-auto px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            {t("partner.common.refresh")}
          </button>
      </div>

      {err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {err}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-gray-500">{t("partner.common.loading")}</div>
      ) : (
        <div className="space-y-3">
          {filteredRequests.length === 0 ? (
            <div className="flex flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-gray-100 text-xl">✦</div>
                <div>
                  <h2 className="font-serif text-xl text-gray-900">{t("partner.emptyState.nextTitle")}</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">{t("partner.emptyState.body")}</p>
                </div>
              </div>
              <div className="flex flex-none flex-wrap items-center gap-3 md:justify-end">
                <Link
                  to="/partner/profile"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm rounded-xl hover:bg-gray-900 transition-all"
                >
                  {t("partner.emptyState.completeProfile")}
                </Link>
                <Link
                  to="/partners"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-800 text-sm rounded-xl hover:border-gray-900 transition-all"
                >
                  {t("partner.emptyState.viewDirectory")}
                </Link>
              </div>
            </div>
          ) : (
            filteredRequests.map((req) => (
              <div key={req.id} className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500">{fmtDate(req.created_at, i18n.language)}</div>
                    <div className="mt-2">{renderer(req, t, i18n.language)}</div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs px-2 py-1 rounded-lg border ${badgeClass(req.status)}`}>
                      {req.status ? t(`partner.status.${req.status}`, { defaultValue: req.status }) : "—"}
                    </span>
                    <button
                      className="px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 hover:bg-gray-200 transition"
                      onClick={() => window.alert(t("partner.requests.detailTodo"))}
                    >
                      {t("partner.requests.viewDetails")}
                    </button>
                  </div>
                </div>

                {req.notes ? (
                  <div className="mt-3 text-xs text-gray-500">
                    {t("partner.common.notes")}: <span className="text-gray-700">{req.notes}</span>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Line({ label, value }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-gray-500">{label}</span><span className="text-right font-medium text-gray-900">{value || "—"}</span></div>;
}
